/**
 * Secure API Wrapper
 *
 * This module provides secure wrappers around API calls to ensure
 * all data is properly sanitized before being sent to the backend.
 *
 * Usage:
 * Instead of calling API directly, use these wrapper functions
 * that automatically sanitize inputs based on the data type.
 */

import { sanitizeObject, sanitizeSearchTerm } from './sanitize';

/**
 * Configuration for field sanitization by entity type
 * Defines which fields contain HTML, URLs, or should be skipped
 */
const SANITIZATION_CONFIG = {
  products: {
    htmlFields: ['description', 'short_description', 'specifications'],
    urlFields: ['image_url', 'video_url'],
    skipFields: ['id', 'created_at', 'updated_at', 'price', 'cost', 'quantity']
  },
  categories: {
    htmlFields: ['description'],
    urlFields: ['image', 'icon'],
    skipFields: ['id', 'parent_id', 'created_at', 'updated_at']
  },
  brands: {
    htmlFields: ['description'],
    urlFields: ['logo', 'image'],
    skipFields: ['id', 'created_at', 'updated_at']
  },
  features: {
    htmlFields: ['description'],
    urlFields: [],
    skipFields: ['id', 'created_at', 'updated_at']
  },
  orders: {
    htmlFields: ['notes', 'admin_notes'],
    urlFields: [],
    skipFields: ['id', 'order_number', 'created_at', 'updated_at', 'total_amount', 'tax_amount', 'discount_amount']
  },
  users: {
    htmlFields: [],
    urlFields: ['avatar', 'profile_image'],
    skipFields: ['id', 'password', 'created_at', 'updated_at', 'last_login_at']
  },
  settings: {
    htmlFields: ['value'], // Settings values might contain HTML
    urlFields: [],
    skipFields: ['id', 'created_at', 'updated_at']
  },
  coupons: {
    htmlFields: ['description', 'terms_and_conditions'],
    urlFields: [],
    skipFields: ['id', 'created_at', 'updated_at', 'discount_value', 'min_purchase_amount', 'max_discount_amount']
  }
};

/**
 * Sanitize data before API POST/PUT requests
 *
 * @param {Object} data - Data object to sanitize
 * @param {string} entityType - Type of entity (products, categories, etc.)
 * @returns {Object} - Sanitized data object
 */
export const sanitizeRequestData = (data, entityType = 'default') => {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const config = SANITIZATION_CONFIG[entityType] || {
    htmlFields: [],
    urlFields: [],
    skipFields: ['id', 'created_at', 'updated_at']
  };

  return sanitizeObject(data, config);
};

/**
 * Sanitize search/filter parameters
 *
 * @param {Object} params - Query parameters object
 * @returns {Object} - Sanitized parameters
 */
export const sanitizeQueryParams = (params) => {
  if (!params || typeof params !== 'object') {
    return params;
  }

  const sanitized = {};

  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') {
      // Keep empty values as-is (they're usually used to reset filters)
      sanitized[key] = value;
    } else if (typeof value === 'string') {
      // Sanitize string parameters (especially search terms)
      if (key === 'search' || key.includes('search') || key === 'query') {
        sanitized[key] = sanitizeSearchTerm(value);
      } else {
        // For other string parameters, basic sanitization
        sanitized[key] = value.trim();
      }
    } else if (typeof value === 'number' || typeof value === 'boolean') {
      // Numbers and booleans are safe
      sanitized[key] = value;
    } else if (Array.isArray(value)) {
      // Sanitize array values
      sanitized[key] = value.map(item =>
        typeof item === 'string' ? item.trim() : item
      );
    } else {
      // For objects, recursive sanitization
      sanitized[key] = sanitizeQueryParams(value);
    }
  }

  return sanitized;
};

/**
 * Secure wrapper for API POST requests
 *
 * @param {Function} apiFunction - Original API function to wrap
 * @param {string} entityType - Type of entity for proper sanitization
 * @returns {Function} - Wrapped API function
 */
export const securePost = (apiFunction, entityType) => {
  return (data, ...args) => {
    const sanitizedData = sanitizeRequestData(data, entityType);
    return apiFunction(sanitizedData, ...args);
  };
};

/**
 * Secure wrapper for API PUT requests
 *
 * @param {Function} apiFunction - Original API function to wrap
 * @param {string} entityType - Type of entity for proper sanitization
 * @returns {Function} - Wrapped API function
 */
export const securePut = (apiFunction, entityType) => {
  return (id, data, ...args) => {
    const sanitizedData = sanitizeRequestData(data, entityType);
    return apiFunction(id, sanitizedData, ...args);
  };
};

/**
 * Secure wrapper for API GET requests with query parameters
 *
 * @param {Function} apiFunction - Original API function to wrap
 * @returns {Function} - Wrapped API function
 */
export const secureGet = (apiFunction) => {
  return (params, ...args) => {
    const sanitizedParams = sanitizeQueryParams(params);
    return apiFunction(sanitizedParams, ...args);
  };
};

/**
 * Create secure API wrapper for an entire API object
 *
 * @param {Object} api - API object with methods
 * @param {string} entityType - Type of entity
 * @returns {Object} - Wrapped API object
 */
export const createSecureAPI = (api, entityType) => {
  const secureAPI = {};

  for (const [key, value] of Object.entries(api)) {
    if (typeof value === 'function') {
      // Determine the method type and wrap accordingly
      if (key.startsWith('create') || key.includes('Create')) {
        secureAPI[key] = securePost(value, entityType);
      } else if (key.startsWith('update') || key.includes('Update') || key.includes('save')) {
        secureAPI[key] = securePut(value, entityType);
      } else if (key.startsWith('get') || key.startsWith('fetch') || key.includes('List')) {
        secureAPI[key] = secureGet(value);
      } else {
        // For other methods, pass through without sanitization
        // (DELETE methods, toggle methods, etc.)
        secureAPI[key] = value;
      }
    } else {
      secureAPI[key] = value;
    }
  }

  return secureAPI;
};

/**
 * Validate file uploads for security
 *
 * @param {File} file - File object to validate
 * @param {Object} options - Validation options
 * @returns {Object} - { valid: boolean, error: string }
 */
export const validateFileUpload = (file, options = {}) => {
  const {
    maxSize = 5 * 1024 * 1024, // 5MB default
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp']
  } = options;

  // Check if file exists
  if (!file) {
    return { valid: false, error: 'No file provided' };
  }

  // Check file size
  if (file.size > maxSize) {
    const maxSizeMB = (maxSize / (1024 * 1024)).toFixed(2);
    return { valid: false, error: `File size must be less than ${maxSizeMB}MB` };
  }

  // Check file type
  if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
    return { valid: false, error: `File type ${file.type} is not allowed` };
  }

  // Check file extension
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));

  if (!hasValidExtension) {
    return { valid: false, error: `File extension must be one of: ${allowedExtensions.join(', ')}` };
  }

  // Check for double extensions (potential security risk)
  const dotCount = (fileName.match(/\./g) || []).length;
  if (dotCount > 1) {
    return { valid: false, error: 'Files with multiple extensions are not allowed' };
  }

  // Check for suspicious filenames
  const suspiciousPatterns = ['.exe', '.php', '.js', '.sh', '.bat', '.cmd'];
  for (const pattern of suspiciousPatterns) {
    if (fileName.includes(pattern)) {
      return { valid: false, error: 'Suspicious file name detected' };
    }
  }

  return { valid: true, error: null };
};

/**
 * Sanitize filename for safe storage
 *
 * @param {string} filename - Original filename
 * @returns {string} - Sanitized filename
 */
export const sanitizeFilename = (filename) => {
  if (!filename || typeof filename !== 'string') {
    return 'file';
  }

  // Remove path separators
  let sanitized = filename.replace(/[\/\\]/g, '');

  // Remove special characters except dots, hyphens, and underscores
  sanitized = sanitized.replace(/[^a-zA-Z0-9._-]/g, '_');

  // Remove leading dots (hidden files)
  sanitized = sanitized.replace(/^\.+/, '');

  // Limit length
  const MAX_FILENAME_LENGTH = 255;
  if (sanitized.length > MAX_FILENAME_LENGTH) {
    const extension = sanitized.substring(sanitized.lastIndexOf('.'));
    const nameWithoutExt = sanitized.substring(0, sanitized.lastIndexOf('.'));
    sanitized = nameWithoutExt.substring(0, MAX_FILENAME_LENGTH - extension.length) + extension;
  }

  return sanitized || 'file';
};

export default {
  sanitizeRequestData,
  sanitizeQueryParams,
  securePost,
  securePut,
  secureGet,
  createSecureAPI,
  validateFileUpload,
  sanitizeFilename
};
